import { Badge } from "./badge";
import { Button } from "./button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./dialog";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Text } from "../primitives/typography";
import { Field, FieldLabel } from "./field";

export interface VideoEditDialogProps {
    open: boolean;
    onClose: () => void;
    /** Dialog heading. Supplied by the app so this component stays translation-agnostic. */
    heading: string;
    /** Accessible name for the title field. Defaults to `heading`. */
    titleLabel?: string;
    /** The title being edited, and every keystroke back out. Owned by the caller. */
    title: string;
    onTitleChange: (title: string) => void;
    /**
     * What the video is about, on the upload itself. A lesson presenting it may still override
     * with its own, so this is the wording every course inherits by default.
     */
    description: string;
    onDescriptionChange: (description: string) => void;
    /** Field labels. Supplied by the app, like `heading`, so this stays translation-agnostic. */
    descriptionLabel: string;
    /** Every tag that may be picked. */
    tagOptions: { id: string; name: string }[];
    /** Which of `tagOptions` are on, and the whole next selection on each toggle. */
    tagIds: string[];
    onTagIdsChange: (tagIds: string[]) => void;
    /**
     * How many content items present this video. Drives the blast-radius line, so it is required
     * rather than defaulted — a missing count would silently render the reassuring branch.
     */
    usedInCount: number;
    onSave: () => void;
    saving?: boolean;
    /** A failed save, already user-facing by the time it arrives here. */
    errorMessage?: string | null;
}

/**
 * Edits a video's title and tags.
 *
 * Presentational and fully controlled: the caller holds the draft and owns the mutation. The two
 * fields save together because the dialog presents them as one edit — a rename that landed while a
 * tag change vanished is the worse half of a partial success — so there is one `onSave` rather than
 * a callback per field.
 */
export const VideoEditDialog = ({
    open,
    onClose,
    heading,
    titleLabel,
    title,
    onTitleChange,
    description,
    onDescriptionChange,
    descriptionLabel,
    tagOptions,
    tagIds,
    onTagIdsChange,
    usedInCount,
    onSave,
    saving,
    errorMessage,
}: VideoEditDialogProps) => {
    const toggleTag = (tagId: string) =>
        onTagIdsChange(
            tagIds.includes(tagId)
                ? tagIds.filter((id) => id !== tagId)
                : [
                      ...tagIds,
                      tagId,
                  ],
        );

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="max-w-x-lg">
                <DialogHeader>
                    <DialogTitle>{heading}</DialogTitle>
                </DialogHeader>
                <Field>
                    <FieldLabel htmlFor="description">Title</FieldLabel>
                    <Input
                        aria-label={titleLabel ?? heading}
                        value={title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") onSave();
                        }}
                        type="text"
                        autoFocus
                    />
                </Field>

                {/*
                 * No Enter-to-save here, unlike the title above: Enter inserts a newline, which is
                 * the only sensible thing a multi-line field can do with it.
                 */}
                <Field>
                    <FieldLabel htmlFor="description">Description</FieldLabel>
                    <Textarea
                        aria-label={descriptionLabel}
                        placeholder={descriptionLabel}
                        value={description}
                        onChange={(e) => onDescriptionChange(e.target.value)}
                        rows={4}
                    />
                </Field>

                {/*
                 * The video's own tags (ADR 0014) — editable whether or not a lesson uses it yet.
                 * They also show on every course presenting the video, through `Course.allTags`.
                 */}
                <Field>
                    <FieldLabel>Tags</FieldLabel>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        {tagOptions.map((tag) => {
                            const on = tagIds.includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    type="button"
                                    aria-pressed={on}
                                    aria-label={tag.name}
                                    onClick={() => toggleTag(tag.id)}
                                >
                                    <Badge variant={on ? "default" : "secondary"} className="cursor-pointer">
                                        {tag.name}
                                    </Badge>
                                </button>
                            );
                        })}
                    </div>
                </Field>

                {/* The blast radius, before the save is accepted. */}
                {usedInCount > 0 && (
                    <Text muted className="text-xs">
                        Used in {usedInCount === 1 ? "1 lesson" : `${usedInCount} lessons`} — these tags show on
                        {usedInCount === 1 ? " its course" : " their courses"} too.
                    </Text>
                )}

                {errorMessage && (
                    <Text role="alert" className="text-destructive text-sm">
                        {errorMessage}
                    </Text>
                )}

                <DialogFooter>
                    <Button variant="outline" className="w-auto" onClick={onClose}>
                        Cancel
                    </Button>
                    {/* Empty is refused rather than saved: a blank title falls back to the raw
                        filename, which is the state this field exists to get out of. */}
                    <Button className="w-auto" disabled={saving || !title.trim()} onClick={onSave}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
